import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    Typography, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    TextField,
    Stack,
    Divider,
    IconButton,
    Alert,
    Tooltip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { Download, Refresh, Description, Close } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client';
import { GENERATE_AVV } from '@company/graphql/mutations/generateAVV';
import { GET_ACTIVE_AVV } from '@company/graphql/queries/getActiveAVV';
import { GERMAN_STATES } from '@/app/constants/germanStates';
import { LocalStorage } from '@/types/enums';
import { useServerTime } from '@company/hooks/useServerTime';

export interface AVVData {
    weisungsbefugter: string;
    bundesland: string;
    kundenname: string;
}

export interface AVVState {
    status: 'not_created' | 'creating' | 'created';
    documentId?: string;
    downloadUrl?: string;
    data?: AVVData;
    fileName?: string;
    generatedAt?: string;
}

interface AVVCreationProps {
    companyData?: {
        name?: string;
        ownerName?: string;
        address?: string;
    };
    initialState?: AVVState;
    onStateChange?: (state: AVVState) => void;
    disabled?: boolean;
    // Option to fetch existing AVV on mount
    fetchExisting?: boolean;
    // New props for onboarding flow
    inlineMode?: boolean; // If true, show generation inline without modal
    autoGenerate?: boolean; // If true, automatically generate AVV when component mounts
}

const AVVCreation: React.FC<AVVCreationProps> = ({
    companyData,
    initialState = { status: 'not_created' },
    onStateChange,
    disabled = false,
    fetchExisting = false,
    inlineMode = false,
    autoGenerate = false
}) => {
    const { serverTime } = useServerTime();
    const [avvState, setAVVState] = useState<AVVState>(initialState);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<AVVData>({
        weisungsbefugter: companyData?.ownerName || '',
        bundesland: companyData?.address || '',
        kundenname: companyData?.name || ''
    });
    const [errors, setErrors] = useState<{[key: string]: string}>({});

    // GraphQL operations
    const [generateAVVMutation] = useMutation(GENERATE_AVV);
    const { data: existingAVV, loading: fetchingAVV, refetch: refetchAVV } = useQuery(GET_ACTIVE_AVV, {
        skip: !fetchExisting,
        fetchPolicy: 'cache-and-network'
    });

    // Update parent when state changes
    useEffect(() => {
        if (onStateChange) {
            onStateChange(avvState);
        }
    }, [avvState, onStateChange]);

    // Handle existing AVV data from query
    useEffect(() => {
        if (existingAVV?.getActiveAVV && avvState.status === 'not_created') {
            const existing = existingAVV.getActiveAVV;
            setAVVState({
                status: 'created',
                documentId: existing.id,
                downloadUrl: existing.downloadUrl || `/files/avv/${existing.id}`, // Use backend URL if available, fallback to manual construction
                fileName: existing.fileName,
                generatedAt: existing.generatedAt,
                data: existing.data ? {
                    kundenname: existing.data.kundenname || '',
                    bundesland: existing.data.bundesland || '',
                    weisungsbefugter: existing.data.weisungsbefugter || ''
                } : undefined
            });

            // Update form data with existing values
            if (existing.data) {
                setFormData({
                    kundenname: existing.data.kundenname || companyData?.name || '',
                    bundesland: existing.data.bundesland || companyData?.address || '',
                    weisungsbefugter: existing.data.weisungsbefugter || companyData?.ownerName || ''
                });
            }
        }
    }, [existingAVV, avvState.status, companyData]);

    // Update form data when company data changes
    useEffect(() => {
        if (companyData && avvState.status === 'not_created') {
            setFormData(prev => ({
                weisungsbefugter: companyData.ownerName || prev.weisungsbefugter,
                bundesland: companyData.address || prev.bundesland,
                kundenname: companyData.name || prev.kundenname
            }));
        }
    }, [companyData, avvState.status]);

    // Auto-generate AVV when in autoGenerate mode
    useEffect(() => {
        if (autoGenerate && avvState.status === 'not_created' && 
            companyData?.name && companyData?.ownerName && companyData?.address && 
            !isLoading) {
            // Auto-generate without showing dialog
            handleAutoGenerate();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoGenerate, avvState.status, companyData, isLoading]);

    const handleAutoGenerate = async () => {
        if (!companyData?.name || !companyData?.ownerName || !companyData?.address) {
            return;
        }

        setIsLoading(true);
        setAVVState(prev => ({ ...prev, status: 'creating' }));
        
        try {
            const result = await generateAVVMutation({
                variables: {
                    avvData: {
                        kundenname: companyData.name,
                        bundesland: companyData.address,
                        weisungsbefugter: companyData.ownerName
                    }
                }
            });

            const response = result.data?.generateAVV;
            
            if (response?.success && response.result) {
                setAVVState({
                    status: 'created',
                    documentId: response.result.id,
                    downloadUrl: response.result.downloadUrl,
                    fileName: response.result.fileName,
                    generatedAt: response.result.generatedAt,
                    data: {
                        kundenname: companyData.name,
                        bundesland: companyData.address,
                        weisungsbefugter: companyData.ownerName
                    }
                });
                
                // Refetch the active AVV if we're tracking existing ones
                if (fetchExisting) {
                    refetchAVV();
                }
            } else {
                throw new Error(response?.error || 'Failed to generate AVV');
            }
        } catch (error: any) {
            console.error('Failed to auto-generate AVV:', error);
            
            // Reset to not_created state so user can try again
            setAVVState(prev => ({ ...prev, status: 'not_created' }));
        } finally {
            setIsLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: {[key: string]: string} = {};

        if (!formData.weisungsbefugter.trim()) {
            newErrors.weisungsbefugter = 'Weisungsbefugter ist erforderlich';
        }
        if (!formData.bundesland.trim()) {
            newErrors.bundesland = 'Bundesland ist erforderlich';
        }
        if (!formData.kundenname.trim()) {
            newErrors.kundenname = 'Kundenname ist erforderlich';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateAVV = () => {
        setDialogOpen(true);
    };

    const handleConfirmGenerate = async () => {
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            const result = await generateAVVMutation({
                variables: {
                    avvData: {
                        kundenname: formData.kundenname,
                        bundesland: formData.bundesland,
                        weisungsbefugter: formData.weisungsbefugter
                    }
                }
            });

            const response = result.data?.generateAVV;
            
            if (response?.success && response.result) {
                setAVVState({
                    status: 'created',
                    documentId: response.result.id,
                    downloadUrl: response.result.downloadUrl,
                    fileName: response.result.fileName,
                    generatedAt: response.result.generatedAt,
                    data: { ...formData }
                });
                
                // Refetch the active AVV if we're tracking existing ones
                if (fetchExisting) {
                    refetchAVV();
                }
            } else {
                throw new Error(response?.error || 'Failed to generate AVV');
            }

            setDialogOpen(false);
        } catch (error: any) {
            console.error('Failed to generate AVV:', error);
            
            // Show proper error message to user
            const errorMessage = error?.message || 'Unbekannter Fehler beim Generieren des AVV-Dokuments';
            alert(`Fehler beim Erstellen des AVV: ${errorMessage}`);
            
            // Reset to not_created state so user can try again
            setAVVState(prev => ({ ...prev, status: 'not_created' }));
            setDialogOpen(false);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!avvState.downloadUrl) return;

        try {
            // Get auth token from localStorage using the correct constant
            const token = localStorage.getItem(LocalStorage.AUTH_TOKEN);
            if (!token) {
                alert('Authentifizierung erforderlich. Bitte melden Sie sich erneut an.');
                return;
            }

            // Construct the full API URL for AVV downloads (use backend base URL, not GraphQL endpoint)
            const apiBaseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
            const fullDownloadUrl = `${apiBaseUrl}${avvState.downloadUrl}`;
            
            console.log('Attempting to download AVV:', {
                downloadUrl: avvState.downloadUrl,
                fullDownloadUrl,
                documentId: avvState.documentId,
                fileName: avvState.fileName
            });

            // Fetch the file with authentication
            const response = await fetch(fullDownloadUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    alert('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.');
                } else if (response.status === 404) {
                    alert('AVV-Dokument nicht gefunden.');
                } else {
                    alert('Fehler beim Herunterladen des AVV-Dokuments.');
                }
                return;
            }

            // Get the blob and create download link
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = avvState.fileName || 'AVV_Dokument.pdf';
            document.body.appendChild(link);
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            console.log('AVV downloaded successfully:', avvState.fileName);
        } catch (error) {
            console.error('Download error:', error);
            alert('Fehler beim Herunterladen des AVV-Dokuments.');
        }
    };

    const handleRegenerate = () => {
        setAVVState(prev => ({ ...prev, status: 'creating' }));
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        if (!isLoading) {
            setDialogOpen(false);
            setErrors({});
        }
    };

    const renderButton = () => {
        // Show loading state when fetching existing AVV
        if (fetchExisting && fetchingAVV) {
            return (
                <Button
                    variant="outlined"
                    disabled={true}
                    size="small"
                    sx={{ textTransform: 'none' }}
                >
                    Lade AVV...
                </Button>
            );
        }

        switch (avvState.status) {
            case 'not_created':
                return (
                    <Button
                        variant="outlined"
                        onClick={handleCreateAVV}
                        disabled={disabled}
                        startIcon={<Description />}
                        size="small"
                        sx={{ textTransform: 'none' }}
                    >
                        AVV erstellen
                    </Button>
                );

            case 'created':
                return (
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            onClick={handleDownload}
                            disabled={disabled}
                            startIcon={<Download />}
                            size="small"
                            sx={{ 
                                textTransform: 'none',
                                height: '36px',
                                px: 2.5,
                                minWidth: '110px'
                            }}
                        >
                            Download
                        </Button>
                        <Tooltip title="Neu generieren" arrow>
                            <IconButton
                                onClick={handleRegenerate}
                                disabled={disabled}
                                size="small"
                                sx={{ 
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.main',
                                        color: 'white'
                                    }
                                }}
                            >
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );

            default:
                return null;
        }
    };

    // Show additional info when AVV exists
    const renderAVVInfo = () => {
        if (avvState.status === 'created' && (avvState.fileName || avvState.generatedAt)) {
            return (
                <Box sx={{ mt: 1 }}>
                    {avvState.fileName && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            Datei: {avvState.fileName}
                        </Typography>
                    )}
                    {avvState.generatedAt && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            Erstellt: {new Date(avvState.generatedAt).toLocaleDateString('de-DE')}
                        </Typography>
                    )}
                </Box>
            );
        }
        return null;
    };

    // Render inline content for onboarding flow
    const renderInlineContent = () => {
        if (isLoading || avvState.status === 'creating') {
            return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                        AVV wird generiert...
                    </Typography>
                </Box>
            );
        }

        if (avvState.status === 'created') {
            return (
                <Box>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>AVV erfolgreich erstellt!</strong><br />
                            Ihr Auftragsverarbeitungsvertrag wurde generiert und steht zum Download bereit.
                        </Typography>
                    </Alert>
                    
                    {renderAVVInfo()}
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            onClick={handleDownload}
                            disabled={disabled}
                            startIcon={<Download />}
                            size="large"
                            sx={{ 
                                textTransform: 'none',
                                px: 4,
                                py: 1.5
                            }}
                        >
                            AVV herunterladen
                        </Button>
                    </Box>
                    
                </Box>
            );
        }

        if (avvState.status === 'not_created') {
            return (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        AVV konnte nicht automatisch erstellt werden.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={handleCreateAVV}
                        disabled={disabled}
                        startIcon={<Description />}
                        sx={{ textTransform: 'none' }}
                    >
                        AVV manuell erstellen
                    </Button>
                </Box>
            );
        }

        return null;
    };

    // Use inline mode for onboarding, regular mode for settings
    if (inlineMode) {
        return renderInlineContent();
    }

    return (
        <>
            {renderButton()}
            {renderAVVInfo()}

            <Dialog 
                open={dialogOpen} 
                onClose={handleDialogClose}
                maxWidth="sm"
                fullWidth
                disableEscapeKeyDown={isLoading}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        {avvState.status === 'creating' ? 'AVV neu generieren' : 'AVV erstellen'}
                    </Typography>
                    {!isLoading && (
                        <IconButton onClick={handleDialogClose} size="small">
                            <Close />
                        </IconButton>
                    )}
                </DialogTitle>

                <DialogContent>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            <strong>Auftragsverarbeitungsvertrag (AVV)</strong><br />
                            Bitte überprüfen und ergänzen Sie die folgenden Angaben für die 
                            automatische Generierung Ihres AVV-Dokuments.
                        </Typography>
                    </Alert>

                    <Stack spacing={3}>
                        <TextField
                            fullWidth
                            label="Weisungsbefugter"
                            value={formData.weisungsbefugter}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, weisungsbefugter: e.target.value }));
                                setErrors(prev => ({ ...prev, weisungsbefugter: '' }));
                            }}
                            error={!!errors.weisungsbefugter}
                            helperText={errors.weisungsbefugter || 'Name der weisungsbefugten Person'}
                            disabled={isLoading}
                        />

                        <FormControl fullWidth error={!!errors.bundesland} disabled={isLoading}>
                            <InputLabel>Bundesland</InputLabel>
                            <Select
                                value={formData.bundesland}
                                label="Bundesland"
                                onChange={(e) => {
                                    setFormData(prev => ({ ...prev, bundesland: e.target.value }));
                                    setErrors(prev => ({ ...prev, bundesland: '' }));
                                }}
                            >
                                {GERMAN_STATES.map((state: string) => (
                                    <MenuItem key={state} value={state}>
                                        {state}
                                    </MenuItem>
                                ))}
                            </Select>
                            {(errors.bundesland || 'Bundesland für Datenschutzbeauftragten') && (
                                <Typography variant="caption" color={errors.bundesland ? 'error' : 'text.secondary'} sx={{ mt: 0.5, mx: 1.75 }}>
                                    {errors.bundesland || 'Bundesland für Datenschutzbeauftragten'}
                                </Typography>
                            )}
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Kundenname"
                            value={formData.kundenname}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, kundenname: e.target.value }));
                                setErrors(prev => ({ ...prev, kundenname: '' }));
                            }}
                            error={!!errors.kundenname}
                            helperText={errors.kundenname || 'Offizieller Firmenname'}
                            disabled={isLoading}
                        />

                        <Divider />

                        <Typography variant="body2" color="text.secondary">
                            Diese Daten werden zur Generierung einer parametrisierten PDF-Datei 
                            verwendet, die als Auftragsverarbeitungsvertrag dient.
                        </Typography>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Button 
                        onClick={handleDialogClose} 
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                    >
                        Abbrechen
                    </Button>
                    <Button 
                        onClick={handleConfirmGenerate}
                        variant="contained"
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                    >
                        {isLoading ? 'PDF wird generiert...' : 'AVV generieren'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AVVCreation; 